import React, { useState } from "react";
import clsx from "clsx";
import { useDropzone } from "react-dropzone";
import { Box, darken, IconButton } from "@mui/material";
import TextField from "@mui/material/TextField";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import AttachFileIcon from "@mui/icons-material/Image";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Resizer from "react-image-file-resizer";

import { useQueryProfile } from "../../../graphql/apollo/useQuery";
import { currentBranch } from "../../../constants/constants";

const SendBtn = styled(IconButton)(({ theme }) => {
  return {
    height: 40,
    width: 40,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover, &:focus": {
      backgroundColor: darken(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.contrastText,
    },
    "& .MuiSvgIcon-root": {
      fontSize: 20,
      marginLeft: 3,
    },
  };
});

interface AddNewMessageProps {
  onSendMessage: (data: any) => void;
}

const MessageInput: React.FC<AddNewMessageProps> = ({ onSendMessage }) => {
  const { t } = useTranslation();
  const { myProfile } = useQueryProfile();
  const [message, setMessage] = useState("");
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/png": [".png"],
      "image/jpg": [".jpg"],
      "image/jpeg": [".jpeg"],
    },
    multiple: false,
    onDrop: async (acceptedFiles) => {
      const file: File = acceptedFiles[0];
      Resizer.imageFileResizer(file, 720, 500, "JPEG", 100, 0, (uri) => {
        onSendMessage({
          variables: {
            input: {
              member: myProfile?.id,
              imageFile: uri,
              isAdmin: false,
              isImage: true,
              branch: currentBranch,
            },
          },
        });
      });
    },
  });

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onClickSendMessage();
    }
  };

  const onClickSendMessage = () => {
    if (message) {
      onSendMessage({
        variables: {
          input: {
            member: myProfile?.id,
            message,
            isAdmin: false,
            isImage: false,
            branch: currentBranch,
          },
        },
      });
      setMessage("");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100vw",
        p: 1,
        background: "white",
      }}
    >
      {message === "" ? (
        <Box sx={{ mr: 2 }}>
          <IconButton
            {...getRootProps({
              className: clsx("dropzone"),
            })}
            style={{ height: 50, width: 50 }}
            size="large"
          >
            <input {...getInputProps()} />
            <AttachFileIcon />
          </IconButton>
        </Box>
      ) : null}
      <TextField
        sx={{
          width: "100%",
          position: "relative",
          transition: "all 0.5s ease",
          "& .MuiOutlinedInput-root": {
            padding: "12px 14px",
          },
        }}
        multiline
        variant="outlined"
        placeholder={t("enterMessage")}
        value={message}
        onChange={(event) => {
          if (event.target.value !== "\n") setMessage(event.target.value);
        }}
        onKeyPress={onKeyPress}
      />
      <Box sx={{ ml: 2 }}>
        <SendBtn onClick={onClickSendMessage} size="large">
          <SendOutlinedIcon />
        </SendBtn>
      </Box>
    </Box>
  );
};

export default MessageInput;
